import { AfterViewInit, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import { Store } from '@ngrx/store';
import { scaleLinear } from 'd3-scale';
import { Map as OlMap } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';

import { DecimalPipe } from '@angular/common';
import html2canvas from 'html2canvas';
import Text from 'ol/style/Text';
import { County } from '../../../../../../../core/classes/county';
import { Territory } from '../../../../../../../core/classes/territory';
import { GenPopover } from '../../../../../../../generated/serverModels/GenPopover';
import { MapService } from '../../../../../../../services/map.service';
import { ReportsService } from '../../../../../../../services/reports.service';
import { Coverage } from '../../../../../../../shared/classes/coverage';
import { MapLayer } from '../../../../../../../shared/classes/map-layer';
import { MapComponent } from '../../../../../../../shared/components/map/map.component';
import { selectCounties, selectTerritories } from '../../../../../../../state/location/location.reducer';
import { BaseStateReportWidget } from '../base-state-report-widget';
import JSZip from 'jszip';

const COVERAGE_SCALE = scaleLinear([0, 20, 40, 60, 80, 100], ['#7b3294', '#c2a5cf', '#f7f7f7', '#a6dba0', '#008837']);
const UNCOVERED_COLOR = '#444';
const MAP_PADDING = [20, 20, 20, 20];

@Component({
  selector: 'tgrid-coverage-map-widget',
  templateUrl: './coverage-map-widget.component.html',
  styleUrls: ['./coverage-map-widget.component.scss'],
  imports: [MatCardModule, MapComponent, MatButtonModule, VulcanIconComponent, DecimalPipe],
})
export class CoverageMapWidgetComponent extends BaseStateReportWidget implements AfterViewInit {
  protected readonly mapId = 'coverage-map-widget';

  private reportsService = inject(ReportsService);
  private store = inject(Store);
  private mapSource = new VectorSource();
  private mapRef = signal<OlMap | null>(null);

  protected stateName = signal<string>('');
  protected year = signal<number>(0);
  protected statePercentile = signal<number>(0);
  private numberPipe = new DecimalPipe('en-US');


  constructor() {
    super();
    this.initializeMapData();
  }

  ngAfterViewInit(): void {
    this.waitForMapReady();
  }

  protected async export(): Promise<void> {
    try {
      const mapUrl = await html2canvas(this.exportContent()?.nativeElement, {
        ignoreElements: (element) => element.classList.contains('legend'),
      }).then(canvas => canvas.toDataURL('image/png'));
      const legendUrl = await html2canvas(this.legendContent()?.nativeElement).then(canvas => canvas.toDataURL('image/png'));

      const zip = new JSZip();
      const baseName = `coverage-map-${this.stateName()}-${this.year()}`;
      zip.file(`${baseName}_map.png`, mapUrl.split(',')[1], { base64: true });
      zip.file(`${baseName}_legend.png`, legendUrl.split(',')[1], { base64: true });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(zipBlob);
      anchor.download = `${baseName}.zip`;
      anchor.click();
      this.snackBar.open('Export downloaded successfully', 'Close', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackBar.open('Export failed. Please try again.', 'Close', { duration: 3000 });
    }
  }

  private initializeMapData(): void {
    const routeParam = <T>(param: string): Observable<T> =>
      this.activatedRoute.params.pipe(
        map(params => params[param]),
        distinctUntilChanged(),
        filter(x => !!x),
        takeUntilDestroyed(),
        shareReplay(1),
      ) as Observable<T>;

    const year$ = routeParam<number>('year');
    const state$ = routeParam<string>('stateAbbr');

    year$.pipe(takeUntilDestroyed()).subscribe(year => this.year.set(year));
    const territory$ = state$.pipe(
      switchMap(abbv =>
        this.store.select(selectTerritories).pipe(
          map(territories => territories.find(t => t.abbreviation === abbv)),
        ),
      ),
      tap(territory => {
        if (territory) {
          this.stateName.set(territory.name);
        }
      }),
      shareReplay(1),
    );

    const countyData = combineLatest([year$, territory$]).pipe(
      switchMap(([year, territory]) =>
        territory ? this.getData(year, territory) : of([]),
      ),
      startWith([]),
    );
    combineLatest([year$, territory$]).pipe(
      switchMap(([, territory]) =>
        territory ? this.reportsService.getStateCoverageData([territory]) : of([]),
      ),
    ).subscribe(data => {
      if (data.length > 0) {
        this.statePercentile.set(data[0].percentage);
      }
    });

    const counties$ = state$.pipe(
      switchMap(abbv =>
        this.store.select(selectCounties).pipe(
          map(counties => counties.filter(c => c.stateAbbreviation === abbv)),
        ),
      ),
    );

    combineLatest([counties$, countyData])
      .pipe(takeUntilDestroyed())
      .subscribe(([counties, coverage]) => this.updateMapLayer(counties, coverage));
  }

  private waitForMapReady(): void {
    //TGrid Map may not have added itself for getMapRef so we need to try again if it has failed.
    timer(100)
      .pipe(
        map(() => MapService.getMapRef(this.mapId)),
        filter(map => !!map),
        take(1),
      )
      .subscribe(map => {
        this.mapRef.set(map);
        map.addLayer(this.createBaseLayer());
      });
  }

  private getData(year: number, territory: Territory): Observable<Coverage[]> {
    return this.reportsService.getCountyCoverageData(year, [territory]).pipe(
      catchError(error => {
        console.error('Error fetching coverage data:', error);
        return of([]);
      }),
    );
  }

  private createBaseLayer(): VectorLayer<VectorSource> {
    return new VectorLayer({
      source: this.mapSource,
      style: (feature, resolution) => {
        const properties = feature.getProperties() as Coverage;

        const isCovered = properties.coveredCustomers > 0 && properties.percentage !== undefined;
        const color = isCovered ? COVERAGE_SCALE(properties.percentage) : UNCOVERED_COLOR;

        return new Style({
          stroke: new Stroke({ color: 'black', width: 1 }),
          fill: new Fill({ color }),
          text: new Text({
            text: (resolution < 3000 && isCovered) ? `${this.numberPipe.transform(properties.percentage, '1.2-2')}%` : '',
            stroke: new Stroke({
              color: 'black',
              width: 1,
            }),
            fill: new Fill({
              color: 'white',
            }),
          }),
        });
      },
      properties: { name: 'All Counties' },
    });
  }

  private updateMapLayer(counties: County[], coverage: Coverage[]): void {
    const mapLayer = this.createMapLayer();

    const coverageMap = new Map(coverage.map(c => [c.fipsCode, c]));
    const features = this.createFeatures(counties, coverageMap, mapLayer);

    this.mapSource.clear();
    this.mapSource.addFeatures(features);
    this.fitMapBounds();
  }

  private createMapLayer(): MapLayer {
    const mapLayer = new MapLayer();
    mapLayer.popoverData = [
      new GenPopover({ displayText: 'County name', propertyName: 'county', title: true, type: 'STRING' }),
      new GenPopover({ displayText: 'State', propertyName: 'abbreviation', type: 'STRING' }),
      new GenPopover({ displayText: 'Coverage Percent', propertyName: 'percentage', type: 'NUMBER', suffix: '%' }),
      new GenPopover({ displayText: 'Total Customers', propertyName: 'totalCustomers', type: 'NUMBER', valueFormat: '1.0-0' }),
      new GenPopover({ displayText: 'Covered Customers', propertyName: 'coveredCustomers', type: 'NUMBER', valueFormat: '1.0-0' }),
    ];
    return mapLayer;
  }

  private createFeatures(counties: County[], coverageMap: Map<string, Coverage>, mapLayer: MapLayer): Feature<Geometry>[] {
    return counties.map(county => new GeoJSON().readFeature({
      type: 'Feature',
      geometry: JSON.parse(county.boundary),
      properties: {
        ...coverageMap.get(county.geoid),
        popoverData: mapLayer.createPopover((coverageMap.get(county.geoid) as unknown as Record<string, unknown>) ?? {}),
      },
    }) as Feature<Geometry>);
  }

  private fitMapBounds(): void {
    const map = MapService.getMapRef(this.mapId);
    const extent = this.mapSource.getExtent();
    if (map && extent && extent.every(val => isFinite(val))) {
      map.getView().fit(extent, { padding: MAP_PADDING });
    }
  }
}
